/**
 * Mapping centralisé WMO → représentation visuelle (emoji warm | Lucide minimal).
 *
 * Le slide météo consomme ces helpers afin de garder le composant
 * d'affichage focalisé sur la mise en page, et de tester séparément
 * la table de correspondance (qui évolue avec les codes WMO ajoutés).
 */
import {
  Sun,
  Cloud,
  CloudSun,
  CloudFog,
  CloudDrizzle,
  CloudRain,
  CloudSnow,
  CloudLightning,
  Moon,
  type LucideIcon,
} from "lucide-react";
import type { WeatherCategory } from "@mem/shared";

/**
 * Emoji warm — diffère day/night uniquement pour les catégories où c'est
 * pertinent (ciel dégagé / partiellement dégagé).
 */
export function pickWarmEmoji(category: WeatherCategory, isDay: boolean): string {
  switch (category) {
    case "clear":
      return isDay ? "☀️" : "🌙";
    case "mainly_clear":
      return isDay ? "🌤️" : "🌙";
    case "partly_cloudy":
      return isDay ? "⛅" : "☁️";
    case "overcast":
      return "☁️";
    case "fog":
      return "🌫️";
    case "drizzle":
      return "🌦️";
    case "rain":
    case "rain_showers":
      return "🌧️";
    case "freezing_rain":
      return "🧊";
    case "snow":
    case "snow_grains":
    case "snow_showers":
      return "❄️";
    case "thunderstorm":
    case "thunderstorm_hail":
      return "⛈️";
    default:
      return "🌡️";
  }
}

/**
 * Icône Lucide minimal — `Moon` est utilisé pour la nuit claire,
 * sinon mapping basé sur la catégorie (jour/nuit sans distinction
 * sur les conditions nuageuses ou précipitantes).
 */
export function pickMinimalIcon(category: WeatherCategory, isDay: boolean): LucideIcon {
  if (!isDay && (category === "clear" || category === "mainly_clear")) {
    return Moon;
  }
  switch (category) {
    case "clear":
    case "mainly_clear":
      return Sun;
    case "partly_cloudy":
      return CloudSun;
    case "overcast":
      return Cloud;
    case "fog":
      return CloudFog;
    case "drizzle":
      return CloudDrizzle;
    case "rain":
    case "rain_showers":
    case "freezing_rain":
      return CloudRain;
    case "snow":
    case "snow_grains":
    case "snow_showers":
      return CloudSnow;
    case "thunderstorm":
    case "thunderstorm_hail":
      return CloudLightning;
    default:
      return Cloud;
  }
}

/**
 * Background CSS du slide selon (catégorie, day/night).
 * Renvoie un `background: ...` complet — incluant fallback radial.
 */
export function pickBackground(category: WeatherCategory, isDay: boolean): string {
  if (!isDay) {
    // Nuit — palette froide universelle (la pluie/orage nocturne reste sombre)
    if (category === "thunderstorm" || category === "thunderstorm_hail") {
      return "radial-gradient(ellipse at center, #2b1a3a 0%, #050208 100%)";
    }
    return "radial-gradient(ellipse at center, #1a2540 0%, #050912 100%)";
  }
  // Jour — palette adaptée à la condition dominante
  switch (category) {
    case "clear":
    case "mainly_clear":
      return "radial-gradient(ellipse at center, #4a7ec0 0%, #15294a 100%)";
    case "partly_cloudy":
      return "radial-gradient(ellipse at center, #3d6ba8 0%, #122140 100%)";
    case "overcast":
    case "fog":
      return "radial-gradient(ellipse at center, #4a5566 0%, #1a2230 100%)";
    case "drizzle":
    case "rain":
    case "rain_showers":
    case "freezing_rain":
      return "radial-gradient(ellipse at center, #2f4860 0%, #0d1622 100%)";
    case "snow":
    case "snow_grains":
    case "snow_showers":
      return "radial-gradient(ellipse at center, #6b7e96 0%, #1f2a38 100%)";
    case "thunderstorm":
    case "thunderstorm_hail":
      return "radial-gradient(ellipse at center, #2b2440 0%, #0a0610 100%)";
    default:
      return "radial-gradient(ellipse at center, #2a4a7a 0%, #0d1a35 100%)";
  }
}
